import { AfterViewInit, Component, effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { CreateOrderStore } from '@apps/mtjr/vendors/app/orders/create-order.service';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { AuthStateStore } from '@libs/core/lib/auth/auth-state.store';
import { NgEventBus } from '@libs/core/lib/event-bus/ng-event-bus';
import { PWAInstallStore } from '@libs/core/lib/pwa/pwa-install.store';
import { AppAlerts } from '@libs/core/lib/utils/app-alerts';
import { AppUpdatesService } from '@libs/core/lib/utils/app-updates.service';
import { ThemeStore } from '@libs/core/lib/utils/theme/theme.store';
import { LanguageSwitcherStore } from '@libs/mtjr/shared/i18n/language-switcher.store';
import { NotificationsStore } from '@libs/mtjr/shared/notifications/notifications.store';
import { UserStore } from '@libs/mtjr/shared/user/user.store';
import { WalletStore } from '@libs/mtjr/shared/wallet/wallet.store';
import { TranslateService } from '@ngx-translate/core';
// import { UserStore } from '@';
import { register } from 'swiper/element/bundle';
// register Swiper elements
register();

@Component({
  selector: 'mav-root',
  template: `
    <ion-app>
      <ion-router-outlet />
    </ion-app>
  `,
  imports: [IonApp, IonRouterOutlet],
})
export class AppComponent implements AfterViewInit {
  themeStore = inject(ThemeStore);
  languageSwitcherStore = inject(LanguageSwitcherStore);
  userStore = inject(UserStore);
  authStore = inject(AuthStateStore);
  pwaInstallStore = inject(PWAInstallStore);
  alerts = inject(AppAlerts);
  appUpdatesService = inject(AppUpdatesService);
  swUpdate = inject(SwUpdate);
  notificationsStore = inject(NotificationsStore);
  router = inject(Router);
  translate = inject(TranslateService);
  eventBus = inject(NgEventBus);
  walletStore = inject(WalletStore);
  store = inject(CreateOrderStore);

  constructor() {
    this.themeStore.initTheme();
    this.languageSwitcherStore.init();
    this.pwaInstallStore.init();
    this.notificationsStore.registerFirebaseMessaging();
    this.appUpdatesService.init();

    effect(async () => {
      if (this.authStore.isAuthenticated()) {
        this.userStore.fetchUserIfNeeded();
        this.walletStore.refreshBalance();
      }
    });

    this.translate.onLangChange.subscribe(() => {
      document.title = this.translate.instant('APP_NAME');
    });

    this.translate.onDefaultLangChange.subscribe(() => {
      document.title = this.translate.instant('APP_NAME');
    });
  }

  ngAfterViewInit(): void {
    this.notificationsStore.onBackgroundMessage((payload) => {
      this.handleNotificationMessage(payload.data);
    });

    this.notificationsStore.onMessage((payload) => {
      this.handleNotificationMessage(payload.data);
    });
  }

  handleNotificationMessage(payload?: { [key: string]: string }) {
    const type = payload?.['type'];
    if (type === 'order.chat') {
      const orderId = payload?.['orderId'];
      const subOrderId = payload?.['subOrderId'];
      if (orderId && subOrderId) {
        this.handleOrderChatEvent({ orderId, subOrderId });
      }
    } else if (type === 'order.status') {
      const orderId = payload?.['orderId'];
      if (orderId) {
        this.eventBus.cast('order:fetch', { orderId });
      }
    } else if (type === 'wallet.credit' || type === 'wallet.debit') {
      if (type === 'wallet.credit') {
        this.alerts.successSnackbarTR('WALLET.BALANCE_UPDATED');
      }
      this.eventBus.cast('wallet:fetch');
    }
  }

  private handleOrderChatEvent(data: { orderId: string; subOrderId: string }) {
    const url = this.router.parseUrl(this.router.url);
    const currentUrl = url.toString();
    if (currentUrl.includes('/orders/' + data.orderId)) {
      // page is opened
      const openedTab = url.queryParams['tab'];
      if (openedTab !== 'chat') {
        // chat is not opened
        this.openOrderChat(data.orderId);
      }
    } else {
      // page is not opened
      this.openOrderChat(data.orderId);
    }
    this.eventBus.cast('order:fetch-chat', data);
  }

  openOrderChat(orderId: string) {
    this.alerts.snackbarTR('ORDERS.INCOMING_CHAT', {
      position: 'top',
      duration: 5000,
      buttons: [
        {
          text: this.translate.instant('ORDERS.OPEN_CHAT'),
          handler: () => {
            this.router.navigate(['/', 'main', 'orders', orderId], {
              queryParams: { tab: 'chat' },
              queryParamsHandling: 'merge',
            });
          },
        },
      ],
    });
  }
}
